import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../utils/emailConfig';

/*
 * EmailJS Setup Instructions:
 *
 * 1. Create an account at https://www.emailjs.com/
 * 2. Add an email service (Gmail, Outlook, etc.) in the Email Services section
 * 3. Create an email template in the Email Templates section
 * 4. Replace the placeholder values in handleSubmit function:
 *    - serviceId: Your EmailJS service ID
 *    - templateId: Your EmailJS template ID
 *    - publicKey: Your EmailJS public key
 *
 * Template Variables to use in EmailJS template:
 * - {{from_name}}: Sender's name
 * - {{from_phone}}: Sender's phone
 * - {{from_email}}: Sender's email
 * - {{message}}: Message content
 * - {{to_name}}: Recipient name (Nakshatra Infra Team)
 */

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // EmailJS configuration from config file
      const { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY } = EMAILJS_CONFIG;

      // Validate configuration
      if (SERVICE_ID === 'your_service_id' || TEMPLATE_ID === 'your_template_id' || PUBLIC_KEY === 'your_public_key') {
        throw new Error('EmailJS not configured. Please update EMAILJS_CONFIG in src/utils/emailConfig.js');
      }

      // Prepare template parameters - match your EmailJS template variables
      const templateParams = {
        name: formData.name,
        from_name: formData.name, // Duplicate for compatibility
        from_email: formData.email || 'Not provided',
        phone: formData.phone, // Add phone number
        message: formData.message,
        title: 'New Contact Form Submission', // Add title for your template
        time: new Date().toLocaleString(), // Add current timestamp
      };

      // Send email using EmailJS
      const result = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      );

      if (result.status === 200) {
        setStatus({ type: 'success', message: 'Message sent successfully! We will contact you soon.' });
        setFormData({ name: '', phone: '', email: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('EmailJS error:', error);
      const errorMessage = error.message.includes('not configured')
        ? 'Email service is not configured yet. Please contact us directly.'
        : 'Something went wrong. Please try again later or contact us directly.';
      setStatus({ type: 'danger', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', minHeight: '100vh', paddingTop: '40px', paddingBottom: '60px' }}>
      <Container className="my-5">
        {/* Header Section */}
        <div className="text-center mb-5">
          <h1 style={{ 
            color: '#2180CE', 
            fontWeight: 700, 
            fontSize: '3rem',
            marginBottom: '1rem',
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
          }}>
            Get In Touch
          </h1>
          <p style={{ 
            fontSize: '1.2rem', 
            color: '#6c757d',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            Have questions about our projects? We're here to help you find your perfect property.
          </p>
        </div>

        <Row className="g-4">
          {/* Contact Info */}
          <Col lg={4} md={12}>
            <Card style={{ 
              border: 'none',
              borderRadius: '20px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #2180CE 0%, #1a6bb8 100%)',
              color: 'white',
              height: '100%'
            }}>
              <Card.Body className="p-4">
                <h3 style={{ fontWeight: 700, marginBottom: '2rem' }}>Contact Information</h3>
                
                <div className="mb-4">
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '1.5rem',
                    padding: '15px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      background: '#FFBE1D',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px',
                      fontSize: '24px'
                    }}>
                      📞
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>Phone</div>
                     <a
  href="tel:+919618889887"
  style={{ fontWeight: 600, textDecoration: "none", color: "inherit" }}
>
  +919618889887
</a>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '1.5rem',
                    padding: '15px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      background: '#FFBE1D',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px',
                      fontSize: '24px'
                    }}>
                      ✉️
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>Email</div>
                      <a
  href="mailto:nakshatrainfraprojects160@gmail.com"
  style={{
    fontWeight: 600,
    wordBreak: "break-word",
    textDecoration: "none",
    color: "inherit",
  }}
>
  nakshatrainfraprojects160@gmail.com
</a>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '1.5rem',
                    padding: '15px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      background: '#FFBE1D',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px',
                      fontSize: '24px'
                    }}>
                      💬
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>WhatsApp</div>
                      <a
  href="https://wa.me/919618889887?text=Hi%20I%20am%20interested%20in%20your%20real%20estate%20projects."
  target="_blank"
  rel="noopener noreferrer"
  style={{
    fontWeight: 600,
    textDecoration: "none",
    color: "inherit",
  }}
>
  +919618889887
</a>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    padding: '15px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      background: '#FFBE1D',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px',
                      fontSize: '24px'
                    }}>
                      📍
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>Location</div>
                      <div style={{ fontWeight: 600 }}>Sri Sai Govardhan Kunj, F 401, Sanjeeva Reddy Nagar, Hyderabad, Telangana 500038</div>
                    </div>
                  </div>
                </div>

                <div style={{ 
                  marginTop: '2rem',
                  padding: '20px',
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)'
                }}>
                  <p style={{ fontSize: '0.9rem', marginBottom: 0, lineHeight: '1.6' }}>
                    ⏰ Our team responds during business hours. For urgent enquiries, please call or WhatsApp us directly.
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Contact Form */}
          <Col lg={8} md={12}>
            <Card style={{ 
              border: 'none',
              borderRadius: '20px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              height: '100%'
            }}>
              <Card.Body className="p-5">
                <h3 style={{ 
                  color: '#2180CE', 
                  fontWeight: 700, 
                  marginBottom: '0.5rem' 
                }}>
                  Send Us a Message
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Fill out the form below and we'll get back to you as soon as possible
                </p>

                {status.message && (
                  <Alert 
                    variant={status.type} 
                    className="mb-4"
                    style={{ 
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                    }}
                  >
                    {status.message}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6} className="mb-4">
                      <Form.Label style={{ fontWeight: 600, color: '#333' }}>Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        style={{ 
                          padding: '12px 20px',
                          borderRadius: '12px',
                          border: '2px solid #e9ecef',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#2180CE'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                    </Col>

                    <Col md={6} className="mb-4">
                      <Form.Label style={{ fontWeight: 600, color: '#333' }}>Phone Number *</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter your phone number"
                        style={{ 
                          padding: '12px 20px',
                          borderRadius: '12px',
                          border: '2px solid #e9ecef',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#2180CE'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: 600, color: '#333' }}>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email (optional)"
                      style={{ 
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: '2px solid #e9ecef',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#2180CE'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: 600, color: '#333' }}>Your Message *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us what you are looking for..."
                      style={{ 
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: '2px solid #e9ecef',
                        transition: 'all 0.3s ease',
                        resize: 'vertical'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#2180CE'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    style={{ 
                      background: 'linear-gradient(135deg, #FFBE1D 0%, #f0b000 100%)',
                      border: 'none',
                      padding: '14px 40px',
                      borderRadius: '12px',
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      color: '#333',
                      boxShadow: '0 4px 15px rgba(255, 190, 29, 0.4)',
                      transition: 'all 0.3s ease',
                      transform: isSubmitting ? 'scale(0.98)' : 'scale(1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(255, 190, 29, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(255, 190, 29, 0.4)';
                    }}
                  >
                    {isSubmitting ? '✈️ Sending...' : '📤 Submit Enquiry'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Map */}
        <Row className="mt-5">
          <Col>
            <Card style={{ 
              border: 'none',
              borderRadius: '20px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <Card.Body className="p-0">
                <div style={{ 
                  background: 'linear-gradient(135deg, #2180CE 0%, #1a6bb8 100%)',
                  padding: '25px 30px',
                  color: 'white'
                }}>
                  <h3 style={{ fontWeight: 700, marginBottom: '5px' }}>📍 Our Location</h3>
                  <p style={{ marginBottom: 0, opacity: 0.9 }}>Visit us at our office in Hyderabad</p>
                </div>
                <div style={{ 
                  height: '400px', 
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  color: '#6c757d'
                }}>
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3806.3684074931452!2d78.44367487516618!3d17.442072483454442!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTfCsDI2JzMxLjUiTiA3OMKwMjYnNDYuNSJF!5e0!3m2!1sen!2sin!4v1766147471678!5m2!1sen!2sin" 
                    width="100%" 
                    height="100%" 
                    style={{border: '0'}} 
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Contact;